import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

/* eslint-disable no-magic-numbers */
import React from 'react';
import { DEFAULT_BREAKPOINT, // We can't get out of useBreakpointValue here as we need to know the breakpoint
// in JS to compute the Cloudinary image URLs
// eslint-disable-next-line no-restricted-syntax
useBreakpointValue, useTheme, useToken } from '@hub/design-system-base';
import { Box } from '@hub/box';
import { Frame } from '@hub/frame';
import { Image as ChakraImage } from '@chakra-ui/image';
import { useIsAboveFold } from '../is-above-fold-provider';
import { Video } from '../video';
import makeImageUrl from './helpers/make-image-url';
import findBestImage from './helpers/find-best-image';
import { asArray } from './helpers/maybe-array'; // series of growing image widths to allow the browser to choose the best

import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const availableSourceSizes = [100, 200, 300, 450, 600, 750, 900, 1072];
const availableDevicePixelRatios = [1, 2];
const availableSources = availableSourceSizes.flatMap(size => availableDevicePixelRatios.map(ratio => ({
  size,
  ratio
})));
// common `sizes`
export const IMAGE_SIZES_THUMBNAIL = 96;
export const IMAGE_SIZES_PRODUCT_CARD = ['(max-width: 447px) 50vw', '(max-width: 703px) 30vw', '(max-width: 927px) calc(25vw - 50px)', '200px'].join(', ');
export const IMAGE_SIZES_FULL_WIDTH = {
  md: '100vw',
  lg: 1072
};
export const IMAGE_SIZES_HERO = {
  md: '100vw',
  lg: 320,
  xl: 384
};
export const IMAGE_SIZES_HERO_PORTRAIT = {
  md: '100vw',
  lg: 215,
  xl: 248
};
export const IMAGE_SIZES_TILE_PRIMARY = {
  sm: '100vw',
  md: 350,
  xl: 400
};
export const IMAGE_SIZES_TILE_SECONDARY = {
  sm: '50vw',
  md: 320,
  xl: 350
};
export const IMAGE_SIZES_DECK_PRIMARY = {
  sm: '100vww',
  md: 300,
  lg: 384,
  xl: 430
};
export const IMAGE_SIZES_DECK_SECONDARY = 200;
export const IMAGE_SIZES_SLIM_CARD_HERO = '(max-width 703px) 116px, 196px';
export const IMAGE_SIZES_COLOR_CARD_HERO = {
  sm: '100vw',
  md: 424,
  xl: 288
};
export const IMAGE_SIZES_DECK_REEL = {
  md: '80vw',
  lg: '30vw',
  xl: 350
};
export const IMAGE_SIZES_GRID_PRIMARY = count => ["(max-width: 447px) ".concat(Math.ceil(100 / Math.min(2, count)), "vw"), "(max-width: 607px) ".concat(Math.ceil(100 / Math.min(3, count)), "vw"), "(max-width: 751px) ".concat(Math.ceil(100 / Math.min(4, count)), "vw"), "(max-width: 775px) ".concat(Math.ceil(100 / Math.min(5, count)), "vw"), "(max-width: 1152px) ".concat(Math.ceil(100 / Math.min(6, count)), "vw"), "".concat(1080 / Math.min(6, count), "px")].join(', ');
export const IMAGE_SIZES_GRID_SECONDARY = 56;
export const IMAGE_SIZES_BROWSE_GRID = {
  sm: '50vw',
  md: '30vw',
  lg: 151,
  xl: 184
};
export const IMAGE_SIZES_BROWSE_LIST = 128;
export const IMAGE_SIZES_GALLERY_MAIN = '(max-width: 927px) 100vw, 485px';
export const IMAGE_SIZES_GALLERY_THUMBNAIL = '(max-width: 927px) 15vw, 100px';
export const IMAGE_SIZES_HALF_HALF = '(max-width: 927px) 100vw, (max-width: 1072px) 50vw, 468px';
/**
 * generate the <img> 'sizes' prop string in various ways
 *
 * sizes is empty will return the max fixed width (normally not what is wanted)
 * sizes is a string will return the string; this allows using custom breakpoints
 * sizes is a number will return the number in pixels; used for images that have a fixed width
 * sizes is an array maps the array values to theme breakpoints; use null to skip a breakpoint
 *   so the breakpoint uses the same size as the previous
 * sizes is an object using theme breakpoint names as keys will map sizes to theme breakpoints
 */

function useImageSizes(sizes) {
  var _theme$__breakpoints;

  const maxFixedWidth = useToken('sizes', 'size-max-fixed');
  const theme = useTheme();
  const breakpoints = (_theme$__breakpoints = theme.__breakpoints) === null || _theme$__breakpoints === void 0 ? void 0 : _theme$__breakpoints.normalized;
  const INHERIT_VALUE_FROM_PREVIOUS_BREAKPOINT_PLACEHOLDER = 'NO VALUE';
  const sizesWithPlaceholders = Array.isArray(sizes) ? [sizes].flatMap(s => s).map(s => s || INHERIT_VALUE_FROM_PREVIOUS_BREAKPOINT_PLACEHOLDER) : [];
  const sizeValues = useToken('sizes', sizesWithPlaceholders);

  if (!breakpoints) {
    return "".concat(maxFixedWidth, "px");
  }

  if (typeof sizes === 'string') {
    return sizes;
  }

  if (typeof sizes === 'number') {
    return "".concat(sizes, "px");
  }

  if (typeof sizes !== 'object') {
    return "".concat(maxFixedWidth, "px");
  }

  if (Array.isArray(sizes)) {
    if (!(breakpoints !== null && breakpoints !== void 0 && breakpoints.length)) {
      return "".concat(maxFixedWidth, "px");
    }

    if (sizeValues.length === 0) {
      return "".concat(maxFixedWidth, "px");
    }

    const [defaultSize] = sizeValues.slice(-1);
    return sizeValues.slice(0, -1).map((size, i) => {
      if (size === null) {
        return null;
      }

      const width = typeof size === 'string' ? size : "".concat(size, "px");
      return "(max-width: calc(".concat(breakpoints[i + 1], " - 1px)) ").concat(width);
    }).filter(s => s !== null).concat(typeof defaultSize === 'string' ? defaultSize : "".concat(defaultSize, "px")).join(', ');
  }

  const entries = Object.entries(breakpoints).filter(_ref => {
    let [k] = _ref;
    return isNaN(parseInt(k));
  });
  return entries.reduce((_ref2, _ref3) => {
    let [result, sizes] = _ref2;
    let [breakpoint, value] = _ref3;

    if (!sizes[breakpoint]) {
      return [result, sizes];
    }

    const {
      [breakpoint]: size
    } = sizes,
          rest = _objectWithoutProperties(sizes, [breakpoint].map(_toPropertyKey));

    if (size === undefined) {
      return [result, sizes];
    }

    const width = typeof size === 'string' ? size : "".concat(size, "px");

    if (Object.keys(rest).length === 0) {
      return [result.concat(width), rest];
    }

    return [result.concat("(max-width: calc(".concat(value, " - 1px)) ").concat(width)), rest];
  }, [[], sizes])[0].join(', ');
}

export function CloudinaryImage(_ref4) {
  var _preferredImage$alt;

  let {
    ratio,
    imageSetOrImage,
    preferredImageType,
    resizeMode,
    maxWidth,
    minWidth,
    sx,
    as,
    imageRef,
    sizes,
    className,
    loadingMode,
    onClick
  } = _ref4;
  const currentRatio = useBreakpointValue(asArray(ratio), DEFAULT_BREAKPOINT);
  const currentPrerredImageType = useBreakpointValue(asArray(preferredImageType), DEFAULT_BREAKPOINT);
  const currentResizeMode = useBreakpointValue(asArray(resizeMode), DEFAULT_BREAKPOINT);
  const preferredImage = findBestImage(imageSetOrImage, currentPrerredImageType);
  const srcset = availableSources.map(srcOption => {
    const src = makeImageUrl(preferredImage, currentRatio, currentResizeMode, srcOption.size, srcOption.ratio); // we can't specify both dpr and width in srcset so we just multiply size and ratio

    return "".concat(src, " ").concat(srcOption.size * srcOption.ratio, "w");
  }).join(', ');
  const imageSizes = useImageSizes(sizes);
  const isAboveFold = useIsAboveFold();
  const defaultLoadingMode = isAboveFold ? 'eager' : 'lazy';
  return /*#__PURE__*/_jsx(Frame, {
    ref: imageRef,
    as: as,
    ratio: ratio,
    minWidth: minWidth,
    maxWidth: maxWidth,
    sx: _objectSpread({
      position: 'relative',
      isolation: 'isolate'
    }, sx),
    "data-id": "cloudinary-image_index_CloudinaryImage_Frame",
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ChakraImage, {
        alt: (_preferredImage$alt = preferredImage.alt) !== null && _preferredImage$alt !== void 0 ? _preferredImage$alt : undefined,
        boxSize: "size-full",
        loading: loadingMode !== null && loadingMode !== void 0 ? loadingMode : defaultLoadingMode,
        srcSet: srcset,
        sizes: imageSizes,
        className: className,
        "data-id": "cloudinary-image_index_CloudinaryImage_ChakraImage"
      }), preferredImage.cinemagraphUrl && /*#__PURE__*/_jsx(Box, {
        sx: {
          position: 'absolute',
          width: '100%',
          height: '100%'
        },
        className: className,
        onClick: onClick,
        "data-id": "cloudinary-image_index_CloudinaryImage_Box",
        children: /*#__PURE__*/_jsx(Video, {
          wide: ratio === '21/9',
          url: preferredImage.cinemagraphUrl,
          "data-id": "cloudinary-image_index_CloudinaryImage_Box_Video"
        })
      })]
    })
  });
}